import { computed, action, makeObservable, observable } from "mobx";
import { isUUID, UUID } from "../../State/EvidenceDB/DatabaseTypes";
import { RecordingViewState } from "./RecordingViewState";
import { ProcessInfo } from "../../State/ProcessInfo";
import { DbRecordingsTableWriter } from "../../State/EvidenceDB/DbRecordingsTableWriter";

interface InjectedContext {
	searchWorkerUrl: string;
}

export class WindowRoutingState {
	_urlHash: string;
	_uploadInProgressPercentage: number | undefined;

	@computed get postHash() {
		return this._urlHash.slice(1);
	}

	@computed get uploadInProgressPercentage() {
		return this._uploadInProgressPercentage;
	}

	@computed get recordingUuid() {
		const postHash = this.postHash;
		const recordingUuid = isUUID(postHash) ? postHash : undefined;
		ProcessInfo.log(`reading recordingUuid from ${postHash}`, recordingUuid);
		return recordingUuid;
	}

	@computed get recordingViewState() {
		return RecordingViewState.forRecordingUuid(this, this.recordingUuid);
	}

	@computed get isRecordingPage() {
		return this.recordingViewState != null;
	}

	@computed get isSettingsPage() {
		return this.postHash === "settings";
	}

	@computed get isPrivacyPolicyPage() {
		return this.postHash === "privacy";
	}

	@computed get isRecordingsPage() {
		return this.postHash === "recordings";
	}

	setUploadInProgressPercentage = action ( (percentage: number | undefined) => {
		this._uploadInProgressPercentage = percentage;
	});

	private setHash = action((page: "settings" | "recordings" | "privacy" | UUID) => {
		this._urlHash = `#${page}`;
	});

	pushPage = (page: "settings" | "recordings" | UUID) => {
		window.history.pushState(undefined, '', `#${page}`);
		this.setHash(page);
	};

	replacePage = (page: "settings" | "recordings" | UUID) => {
		window.history.replaceState(undefined, '', `#${page}`);
		this.setHash(page);
	};

	constructor(
		public readonly injectedContext: InjectedContext,
		public readonly recordingsTableWriter: DbRecordingsTableWriter,
	) {
		this._urlHash = window.location.hash.length > 0 ?
			window.location.hash.slice(1) :
			`#${recordingsTableWriter?.recordingInProgress?.recordingUuid}`;
		ProcessInfo.log(`Constructing  WindowRoutingState`, recordingsTableWriter?.recordingInProgress, this._urlHash);
		makeObservable(this, {
			"_urlHash": observable,
			"_uploadInProgressPercentage": observable
		});

		window.addEventListener('popstate', () => {
			this.setHash(new URL(window.location.href).hash.slice(1) as "settings" | "recordings" | "privacy" | UUID);
		})
		window.addEventListener('hashchange', () => {
			this.setHash(new URL(window.location.href).hash.slice(1) as "settings" | "recordings" | "privacy" | UUID);
		})
	}
}
